import clsx from 'clsx';
import { memo, type ReactElement } from 'react';

import type { SharedIconProps } from '../../types/SharedIconProps';
import type { SharedLinkProps } from '../../types/SharedLinkProps';
import type { TextVariant } from '../../types/TextVariant';
import type { TextTone } from '../Text/types';

import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import './TextBlock.css';

export type TextBlockStroke = 'default' | 'strong';

export type TextBlockLinkProps = Pick<SharedLinkProps, 'href' | 'external'>;

export type TextBlockProps = {
  text: string;
  icon?: SharedIconProps;
  link?: TextBlockLinkProps;
  secondaryText?: string;
  secondaryTone?: TextTone;
  secondaryTooltip?: string;
  stroke?: TextBlockStroke;
  tooltip?: string;
  /**
   * Clamps each line after this many lines; `true` clamps after one. Text
   * supplies its own overflow tooltip, so pair it with `tooltip` only when the
   * hover copy differs from the string itself.
   */
  truncate?: boolean | number;
  /** Overrides the primary text's variant instead of deriving it from `stroke`. */
  variant?: TextVariant;
  __private?: {
    /** Forwarded to each `Text` — see `Text`'s `__private.inInteractive`. */
    inInteractive?: boolean;
  };
};

function wrapTooltip(content: ReactElement, tooltip?: string) {
  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip tooltip={tooltip} __private={{ wrapTrigger: true }}>
      {content}
    </Tooltip>
  );
}

export const TextBlock = memo(
  ({
    text,
    icon,
    link,
    secondaryText,
    secondaryTone = 'subdued',
    secondaryTooltip,
    stroke = 'default',
    tooltip,
    truncate,
    variant,
    __private,
  }: TextBlockProps) => {
    const href = link?.href;
    const textPrivate = { inInteractive: __private?.inInteractive };
    const external = link?.external ?? false;
    const primaryVariant =
      variant ?? (stroke === 'strong' ? 'body-semi-m' : 'body-m');
    const linkVariant = stroke === 'strong' ? 'default' : 'subdued';
    const isInline = !icon && !secondaryText;
    const primaryAs = isInline ? 'span' : 'p';

    return (
      <Layout
        align={isInline ? 'center' : 'start'}
        className={clsx(
          'eds-text-block',
          isInline && 'eds-text-block--inline',
          truncate && 'eds-text-block--truncate',
        )}
        spacing="xs"
      >
        {icon && (
          <Layout
            align="center"
            className="eds-text-block--icon"
            spacing="none"
          >
            <Icon
              filled={icon.filled}
              icon={icon.key}
              label={icon.label}
              size={icon.size ?? 's'}
            />
          </Layout>
        )}
        <Layout
          className="eds-text-block--content"
          direction="column"
          flex="1"
          spacing="none"
        >
          {wrapTooltip(
            href ? (
              <Link
                external={external}
                href={href}
                size="m"
                text={text}
                variant={linkVariant}
              />
            ) : (
              <Text
                as={primaryAs}
                truncate={truncate}
                variant={primaryVariant}
                __private={textPrivate}
              >
                {text}
              </Text>
            ),
            tooltip,
          )}
          {secondaryText &&
            wrapTooltip(
              <Text
                as="p"
                tone={secondaryTone}
                truncate={truncate}
                variant="body-m"
                __private={textPrivate}
              >
                {secondaryText}
              </Text>,
              secondaryTooltip,
            )}
        </Layout>
      </Layout>
    );
  },
);

TextBlock.displayName = 'TextBlock';
